<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';

import { getImageUrlWithCustomWidth } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Project } from '@/typings/projects/Project.ts';
import { ListingTypeEnum } from '@/enums/ListingType.ts';
import { useProject } from '@/composables/useProject.ts';

const { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const slots = useSlots();
const { getStartingPrice: getProjectStartingPrice } = useProject();

const props = defineProps<{
  project: Project;
  href: string;
  h4Title: boolean;
  listingType: ListingTypeEnum;
}>();

const emit = defineEmits<{
  click: [project: Project];
}>();

const isAvailable = computed(() => {
  return props.project.availableUnits !== null &&
         props.project.availableUnits !== undefined &&
         props.project.availableUnits > 0;
});

const imageIsLoading = ref(true);

function handleClick(event: Event) {
  // Empêcher le clic si pas d'unités disponibles
  if (!isAvailable.value) {
    event.preventDefault();
    return;
  }
  
  if (!props.href) {
    event.preventDefault();
    emit('click', props.project);
  }
}

const getStartingPrice = computed(() => {
  return getProjectStartingPrice(props.project, props.listingType);
});

function getProjectImage(imageUrl: string) {
  if (imageUrl) {
    return getImageUrlWithCustomWidth(imageUrl, 500);
  }
}

function projectHasPromotion(project: Project): boolean {
  return Boolean(project.hasPromotion);
}
</script>

<template>
  <div class="cardTopImage-container">
    <component :is="href && isAvailable ? 'a' : 'div'"
               :href="(href && isAvailable) ? href : undefined"
               :class="{ 'projectCard--disabled': !isAvailable }"
               @click="handleClick">
      <div class="cardTopImage cardTopImage_projectLinkCard">
        <div>
          <div class="cardTopImage-headerContainer">
            <div>
              <h4 v-if="h4Title"
                  class="cardTopImage-heading">
                {{ project.localizedName }}
              </h4>
              <h3 v-else
                  class="cardTopImage-heading">
                {{ project.localizedName }}
              </h3>
              <div v-if="project.localizedRegionName" 
                   class="cardTopImage-regionName">
                {{ project.localizedRegionName }}
              </div>
            </div>
            <div class="cardTopImage-statusContainer">
              <div v-if="isAvailable && projectHasPromotion(project)"
                   class="tagPromotion">
                {{ t('unitSummary.promotion') }}
              </div>
            </div>
          </div>
          <div class="cardTopImage-imageContainer">
            <img :src="getProjectImage(project.planImageUrl)"
                 alt=""
                 class="cardTopImage-image"
                 :data-image-is-loading="imageIsLoading"
                 @load="imageIsLoading = false" />
          </div>
          <div class="cardTopImage-contentContainer"> 
            <dl class="projectLinkCard-data">
              <div class="projectLinkCard-dataGroup">
                <dt>{{ t('browsingApp.units.availableUnits', project.availableUnits ?? 0) }}</dt>
                <dd v-if="isAvailable">{{ project.availableUnits ?? 0 }}</dd>
                <dd v-else>{{ t('browsingApp.buildings.noAvailableUnits') }}</dd>
              </div>
              <div class="projectLinkCard-dataGroup">
                <dt>{{ t('browsingApp.startingAt') }}</dt>
                <dd>{{ getStartingPrice }}</dd>
              </div>
              <div v-if="project.dogAllowed"
                   class="projectLinkCard-dataGroup projectLinkCard-dogsAllowed">
                {{ t('browsingApp.projects.dogsAllowed') }}
              </div>

              <div v-if="slots['extra-info']"
                   class="projectLinkCard-extraInfo">
                <slot name="extra-info">
                </slot>
              </div>
            </dl>
          </div>
          <div v-if="slots['footer']"
               class="projectLinkCard-footer">
            <slot name="footer">
            </slot>
          </div>
        </div>
        <span v-if="href && isAvailable"
              class="projectLinkCard-viewAction">
          {{ t('browsingApp.projects.viewProject') }}
          <span
            class="projectLinkCard-arrow">
            <svg width="17"
                 height="14"
                 viewBox="0 0 17 14"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M9.60179 0.0012207L8.32797 1.25257L12.9042 5.74763H0.832031V7.51625H12.9042L8.32797 12.0122L9.60179 13.2631L16.3518 6.63216L9.60179 0.0012207Z"
                    fill="currentColor" />
            </svg>
          </span>
        </span>
      </div>
    </component>
  </div>
</template>

<style lang="scss" scoped>
.cardTopImage-container{
  a{
    text-decoration: none;
    color:var(--gray-950);

    &:hover{
      .cardTopImage_projectLinkCard{
        outline-color: var(--primary-500);
        outline-offset: -1px;
      }
      .projectLinkCard-arrow {
        transform: translateX(2px);
      }
    }
  }
}

.cardTopImage_projectLinkCard {
  border: 2px solid var(--gray-200);
  border-radius: .8rem;
  background: #ffffff;
  padding: 3.2rem;
  outline: 4px solid transparent;
  outline-offset: -4px;
  transition: outline-color .2s, outline-offset .2s;

  .cardTopImage-headerContainer {
    display: flex;
    align-items: baseline;
    gap: 1.6rem;
    margin-bottom: 1.6rem;
    justify-content: space-between;
  }

  .cardTopImage-statusContainer {
    display: flex;
    justify-content: flex-start;
    flex-shrink: 0;
  }

  .cardTopImage-heading {
    font-size: 2rem;
    font-weight: 600;
    font-family: "Inter", "Roboto", sans-serif;
    margin: 0;
    text-decoration: none;
  }

  .cardTopImage-titleGroup {
    margin: 0;
  }

  .cardTopImage-contentContainer {
    padding: 3.2rem 0 0 0;
    display: block;
  }

  .cardTopImage-locationGroup {
    margin: 1.6rem 0;
  }

  .cardTopImage-regionName {
    font-size: 1.8rem;
    font-weight: 600;
  }

  .cardTopImage-subheading {
    font-size: 1.6rem;
    font-weight: 500;
  }

  .cardTopImage-image {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
  }


  .projectLinkCard-data {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;
    margin-bottom: 1.6rem;

    @media screen and (max-width: 500px) {
      grid-template-columns: 1fr;
    }
  }

  .projectLinkCard-dataGroup {
    dt {
      font-size: 1.4rem;
    }

    dd {
      font-size: 1.6rem;
      font-weight: 700;
      margin: 0;
    }
  }

  .projectLinkCard-dogsAllowed {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .projectLinkCard-viewAction {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--primary-600);
    text-decoration: none;
    margin-top: 1.6rem;

    &:hover .projectLinkCard-arrow {
      transform: translateX(2px);
    }
  }

  .projectLinkCard-arrow {
    font-size: 1.25rem;
    transition: transform 0.2s;
  }
}

// Styles pour les cartes désactivées
.projectCard--disabled {
  pointer-events: none;
  
  .cardTopImage_projectLinkCard {
    opacity: 0.6;
    filter: grayscale(0.3);
    cursor: not-allowed;
    
    &:hover {
      outline-color: transparent !important;
    }
  }
}

@media screen and (max-width: 384px){
  .cardTopImage_projectLinkCard{
    padding:1.6rem;
    
     .cardTopImage-headerContainer{
      flex-direction: column;
     }
  }
}
</style>
